import React, { useEffect, useMemo, useState, useCallback } from "react";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUser,
  getLoggedUserDetails,
  loadUserDetails,
} from "../../app/store/auth";
import {
  useLoadCandidateAppsQuery,
  useLoadAppRuningTasksQuery,
  useLoadAppDocumentsQuery,
  useLoadCandidatesByFilterMutation,
} from "../../app/store/candidateApiSlice";
import { getFieldData, getFormatedNumber } from "../../Services/appService";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";

const ROUTE_ONBOARDING = [
  { name: "Task Management", url: "" },
  { name: "Onboarding Steps", url: "" },
];

const REPORTING_STYLES = {
  shell: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  heroCard: {
    borderRadius: "22px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.05)",
    overflow: "hidden",
  },
  heroHeader: {
    padding: "20px 22px 16px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  },
  titleRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "15px",
    border: "1px solid rgba(191, 219, 254, 1)",
  },
  title: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "6px",
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#64748b",
    margin: 0,
  },
  heroBody: {
    padding: "18px 22px 22px 22px",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "16px",
  },
  heroSections: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  statCard: {
    borderRadius: "18px",
    padding: "14px 16px",
    background: "#ffffff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    boxShadow: "0 10px 26px rgba(37, 99, 235, 0.06)",
  },
  statLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "6px",
  },
  statValue: {
    fontSize: "22px",
    fontWeight: 800,
    lineHeight: 1.1,
    color: "#0f172a",
  },
  statHint: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#64748b",
    marginTop: "6px",
  },
  sectionGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: "16px",
  },
  sectionGridBottom: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "16px",
  },
  card: {
    borderRadius: "24px",
    border: "1px solid rgba(203, 213, 225, 0.9)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    boxShadow: "0 18px 38px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
    height: "100%",
    position: "relative",
  },
  cardHeader: {
    padding: "20px 22px 16px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
    background: "linear-gradient(135deg, rgba(239, 246, 255, 0.85) 0%, rgba(255, 255, 255, 0.98) 65%)",
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  cardTitleWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  cardTitleIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    color: "#1d4ed8",
    border: "1px solid rgba(191, 219, 254, 1)",
    boxShadow: "0 10px 20px rgba(37, 99, 235, 0.08)",
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#0f172a",
    margin: 0,
  },
  cardSubtitle: {
    fontSize: "12px",
    lineHeight: 1.55,
    color: "#64748b",
    marginTop: "2px",
    fontWeight: 600,
  },
  cardBody: {
    padding: "20px 22px 22px 22px",
  },
  detailGrid: {
    display: "grid",
    gap: "10px",
  },
  compactDetailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "12px",
  },
  detailRow: {
    display: "grid",
    gridTemplateColumns: "minmax(155px, 0.52fr) minmax(0, 1fr)",
    gap: "16px",
    alignItems: "start",
    padding: "12px 14px",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    borderRadius: "16px",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.03)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  },
  detailLabel: {
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  detailValue: {
    fontSize: "14px",
    lineHeight: 1.55,
    color: "#0f172a",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontWeight: 600,
  },
  compactDetailRow: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "14px 16px",
    border: "1px solid rgba(226, 232, 240, 0.9)",
    borderRadius: "16px",
    background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
    boxShadow: "0 8px 22px rgba(15, 23, 42, 0.03)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease",
    minWidth: 0,
  },
  compactDetailTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    minWidth: 0,
  },
  compactDetailValue: {
    fontSize: "16px",
    lineHeight: 1.45,
    color: "#0f172a",
    fontWeight: 700,
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  docList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  docButton: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    textAlign: "left",
    border: "1px solid rgba(203, 213, 225, 0.9)",
    borderRadius: "18px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: "14px 16px",
    color: "#1d4ed8",
    fontSize: "14px",
    fontWeight: 700,
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
    transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  },
  docIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    color: "#2563eb",
    flexShrink: 0,
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "999px",
    border: "1px solid rgba(191, 219, 254, 1)",
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "7px 12px",
    fontSize: "12px",
    fontWeight: 700,
  },
  emptyState: {
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#64748b",
    margin: 0,
  },
};

export default function ReportingDetails() {
  const [hoveredDetailRow, setHoveredDetailRow] = useState("");
  const [hoveredDocId, setHoveredDocId] = useState(null);

  const userDetailsSelector = useSelector(getLoggedUserDetails);

  const [handleDownloadFile] = useDocDownload();

  const dispatch = useDispatch();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const currentUserId = currentUser?.id;

  useEffect(() => {
    dispatch(setCurrentRoute(ROUTE_ONBOARDING));
  }, [dispatch]);

  useEffect(() => {
    if (!userDetailsSelector && currentUserId) {
      dispatch(loadUserDetails(currentUserId));
    }
  }, [dispatch, userDetailsSelector, currentUserId]);

  const [loadCandidatesByFilter, { data: candidateSelector = [] }] = useLoadCandidatesByFilterMutation();

  useEffect(() => {
    if (currentUserId > 0) {
      const frmBody = new FormData();
      frmBody.append("id", currentUserId);
      frmBody.append("name", "");
      frmBody.append("email", "");
      frmBody.append("entity", "");
      frmBody.append("type", "candidate");
      frmBody.append("filterType", "single");
      loadCandidatesByFilter(frmBody);
    }
  }, [currentUserId, loadCandidatesByFilter]);

  const candidate = useMemo(() => {
    return candidateSelector[0] || null;
  }, [candidateSelector]);

  useEffect(() => {
    let entType = "";
    if (candidate) {
      entType = getFieldData(
        candidate.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
    } else if (userDetailsSelector) {
      entType = getFieldData(
        userDetailsSelector.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
    }

    if (entType) {
      const count = entType !== "C2C Basis" ? "5" : "4";
      dispatch(setCurrentPageTitle(`STEP-${count} : Reporting Details`));
    }
  }, [candidate, userDetailsSelector, dispatch]);

  // RTK Query hooks for apps, tasks, and documents
  const { data: candidateApps = [], refetch: refetchCandidateApps } = useLoadCandidateAppsQuery(currentUserId, { skip: !currentUserId });

  const wfId = useMemo(() => {
    if (!candidateApps || candidateApps.length === 0) return 0;
    const mainWorkflow = candidateApps.filter((x) => x.workflowType === 1);
    return mainWorkflow[0]?.oldWorkflowId || 0;
  }, [candidateApps]);

  const { data: appTasks = [] } = useLoadAppRuningTasksQuery(
    { wfId, userId: currentUserId },
    { skip: !wfId || !currentUserId }
  );

  const { data: appDocuments = [], refetch: refetchAppDocuments } = useLoadAppDocumentsQuery(
    { wfId, userId: currentUserId },
    { skip: !wfId || !currentUserId }
  );

  const wfTask = useMemo(() => {
    if (!appTasks || appTasks.length === 0) return null;
    const filterTasks = appTasks.filter((x) => x.taskType === 1 && x.wiid > 0);
    return filterTasks[0] || null;
  }, [appTasks]);

  const rptDocs = useMemo(() => {
    if (!appDocuments || appDocuments.length === 0) return [];
    return appDocuments.filter(
      (x) => x.uploadType === "reporting" && x.sendStatus === true && x.deleteFlag !== true
    );
  }, [appDocuments]);

  const handleDocumentLoad = useCallback(() => {
    if (currentUserId > 0) {
      refetchCandidateApps();
      refetchAppDocuments();
    }
  }, [currentUserId, refetchCandidateApps, refetchAppDocuments]);

  const item = candidate || null;
  const agencyName = useMemo(() => {
    return item && getFieldData(item.performerData, "projectengagement", "AgencyName");
  }, [item]);
  const positionTitle = useMemo(() => {
    return item && getFieldData(item.performerData, "projectengagement", "PositionTitle");
  }, [item]);

  const reportingInfo = useMemo(
    () => [
      {
        label: "Position Title",
        value: positionTitle,
      },
      {
        label: "Reporting Time",
        value:
          wfTask &&
          getFieldData(wfTask.stateData, "reportingdetails", "ReportingTime"),
      },
      {
        label: "Dress Code",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "DressCode"),
      },
      {
        label: "Parking",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "Parking"),
      },
      {
        label: "Notes",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "Notes"),
      },
    ],
    [wfTask, positionTitle]
  );

  const workLocationInfo = useMemo(
    () => [
      {
        label: "Address Line 1",
        value:
          wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "AddressLine1"),
      },
      {
        label: "Address Line 2",
        value:
          wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "AddressLine2"),
      },
      {
        label: "City",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "City"),
      },
      {
        label: "State",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "State"),
      },
      {
        label: "Zip Code",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "Zip"),
      },
    ],
    [wfTask]
  );

  const reportingToInfo = useMemo(
    () => [
      {
        label: "Name",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Name"),
      },
      {
        label: "Title",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Title"),
      },
      {
        label: "Phone",
        value:
          wfTask &&
          getFormatedNumber(getFieldData(wfTask.stateData, "reportingto", "Phone")),
      },
      {
        label: "Email",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Email"),
      },
    ],
    [wfTask]
  );

  const summary = useMemo(() => {
    return {
      agency: agencyName || "--",
      startDate:
        item && item.duration?.startDate ? (
          <Moment format="MMM Do YYYY">{item.duration.startDate}</Moment>
        ) : (
          "--"
        ),
      endDate:
        item && item.duration?.endDate ? (
          <Moment format="MMM Do YYYY">{item.duration.endDate}</Moment>
        ) : (
          "--"
        ),
      documents: rptDocs.length,
      contact:
        (wfTask && getFieldData(wfTask.stateData, "reportingto", "Name")) || "--",
    };
  }, [agencyName, item, rptDocs.length, wfTask]);

  const renderCompactDetailRows = useCallback((rows, groupKey) => (
    <div style={REPORTING_STYLES.compactDetailGrid}>
      {rows.map((row) => (
        <div
          key={row.label}
          onMouseEnter={() => setHoveredDetailRow(`${groupKey}-${row.label}`)}
          onMouseLeave={() => setHoveredDetailRow("")}
          style={{
            ...REPORTING_STYLES.compactDetailRow,
            ...(hoveredDetailRow === `${groupKey}-${row.label}`
              ? {
                transform: "translateY(-1px)",
                boxShadow: "0 14px 26px rgba(37, 99, 235, 0.08)",
              }
              : {}),
          }}
        >
          <div style={REPORTING_STYLES.compactDetailTop}>
            <div style={REPORTING_STYLES.detailLabel}>{row.label}</div>
          </div>
          <div style={REPORTING_STYLES.compactDetailValue}>{row.value || "--"}</div>
        </div>
      ))}
    </div>
  ), [hoveredDetailRow]);

  return (
    <React.Fragment>
      <div className="col-md-12 mt-3">
        <div style={REPORTING_STYLES.shell}>
          <div className="card border-1 shadow-none" style={REPORTING_STYLES.heroCard}>
            <div className="card-header border-1" style={REPORTING_STYLES.heroHeader}>
              <div style={REPORTING_STYLES.titleRow}>
                <span style={REPORTING_STYLES.titleIcon}>
                  <i className="fa fa-map-marker" aria-hidden="true"></i>
                </span>
                <div style={REPORTING_STYLES.title}>Reporting Details</div>
              </div>
              {/* <p style={REPORTING_STYLES.subtitle}>
                Review where to report, when to start, supporting documents, and who you should connect with on day one.
              </p> */}
            </div>
            <div className="card-body border-1" style={REPORTING_STYLES.heroBody}>
              <div style={REPORTING_STYLES.statsRow}>
                <div style={REPORTING_STYLES.statCard}>
                  <div style={REPORTING_STYLES.statLabel}>Agency</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.agency}</div>
                  <div style={REPORTING_STYLES.statHint}>Current agency or reporting location on file.</div>
                </div>
                <div style={REPORTING_STYLES.statCard}>
                  <div style={REPORTING_STYLES.statLabel}>Start Date</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.startDate}</div>
                  <div style={REPORTING_STYLES.statHint}>Planned onboarding reporting start date.</div>
                </div>
                <div style={REPORTING_STYLES.statCard}>
                  <div style={REPORTING_STYLES.statLabel}>End Date</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.endDate}</div>
                  <div style={REPORTING_STYLES.statHint}>Expected assignment completion date on file.</div>
                </div>
                <div style={REPORTING_STYLES.statCard}>
                  <div style={REPORTING_STYLES.statLabel}>Documents</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.documents}</div>
                  <div style={REPORTING_STYLES.statHint}>Reporting-related files currently available.</div>
                </div>
                <div style={REPORTING_STYLES.statCard}>
                  <div style={REPORTING_STYLES.statLabel}>Reporting To</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.contact}</div>
                  <div style={REPORTING_STYLES.statHint}>Primary contact listed for reporting instructions.</div>
                </div>
              </div>
              <div style={REPORTING_STYLES.heroSections}>
                <div style={REPORTING_STYLES.sectionGrid}>
                  <div className="card border-1 shadow-none" style={REPORTING_STYLES.card}>
                    <div className="card-header border-1" style={REPORTING_STYLES.cardHeader}>
                      <div style={REPORTING_STYLES.cardHeaderRow}>
                        <div style={REPORTING_STYLES.cardTitleWrap}>
                          <span style={REPORTING_STYLES.cardTitleIcon}>
                            <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                          </span>
                          <div>
                            <h3 style={REPORTING_STYLES.cardTitle}>Documents</h3>
                            <div style={REPORTING_STYLES.cardSubtitle}>Files shared for reporting and day-one prep</div>
                          </div>
                        </div>
                        <button
                          className="btn btn-sm"
                          style={REPORTING_STYLES.refreshButton}
                          onClick={handleDocumentLoad}
                        >
                          <i className="fa fa-refresh" aria-hidden="true"></i>
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="card-body border-1" style={REPORTING_STYLES.cardBody}>
                      {rptDocs.length > 0 ? (
                        <div style={REPORTING_STYLES.docList}>
                          {rptDocs.map((doc) => (
                            <button
                              key={doc.id}
                              type="button"
                              style={{
                                ...REPORTING_STYLES.docButton,
                                ...(hoveredDocId === doc.id
                                  ? {
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 16px 30px rgba(37, 99, 235, 0.08)",
                                  }
                                  : {}),
                              }}
                              title="Download File"
                              onClick={() => handleDownloadFile(doc)}
                              onMouseEnter={() => setHoveredDocId(doc.id)}
                              onMouseLeave={() => setHoveredDocId(null)}
                            >
                              <span style={REPORTING_STYLES.docIcon}>
                                <i className="fa fa-file-text-o" aria-hidden="true"></i>
                              </span>
                              <span>{doc.documentName}</span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p style={REPORTING_STYLES.emptyState}>
                          No reporting documents are available yet. Refresh this section after new files are shared.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="card border-1 shadow-none" style={REPORTING_STYLES.card}>
                    <div className="card-header border-1" style={REPORTING_STYLES.cardHeader}>
                      <div style={REPORTING_STYLES.cardHeaderRow}>
                        <div style={REPORTING_STYLES.cardTitleWrap}>
                          <span style={REPORTING_STYLES.cardTitleIcon}>
                            <i className="fa fa-briefcase" aria-hidden="true"></i>
                          </span>
                          <div>
                            <h3 style={REPORTING_STYLES.cardTitle}>Reporting Details</h3>
                            <div style={REPORTING_STYLES.cardSubtitle}>Role, schedule, and reporting expectations</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body border-1" style={REPORTING_STYLES.cardBody}>
                      {renderCompactDetailRows(reportingInfo, "assignment")}
                    </div>
                  </div>
                </div>

                <div style={REPORTING_STYLES.sectionGridBottom}>
                  <div className="card border-1 shadow-none" style={REPORTING_STYLES.card}>
                    <div className="card-header border-1" style={REPORTING_STYLES.cardHeader}>
                      <div style={REPORTING_STYLES.cardHeaderRow}>
                        <div style={REPORTING_STYLES.cardTitleWrap}>
                          <span style={REPORTING_STYLES.cardTitleIcon}>
                            <i className="fa fa-map-marker" aria-hidden="true"></i>
                          </span>
                          <div>
                            <h3 style={REPORTING_STYLES.cardTitle}>Customer Address</h3>
                            <div style={REPORTING_STYLES.cardSubtitle}>Where you are expected to report on-site</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body border-1" style={REPORTING_STYLES.cardBody}>
                      {renderCompactDetailRows(workLocationInfo, "location")}
                    </div>
                  </div>

                  <div className="card border-1 shadow-none" style={REPORTING_STYLES.card}>
                    <div className="card-header border-1" style={REPORTING_STYLES.cardHeader}>
                      <div style={REPORTING_STYLES.cardHeaderRow}>
                        <div style={REPORTING_STYLES.cardTitleWrap}>
                          <span style={REPORTING_STYLES.cardTitleIcon}>
                            <i className="fa fa-user-circle-o" aria-hidden="true"></i>
                          </span>
                          <div>
                            <h3 style={REPORTING_STYLES.cardTitle}>Reporting To</h3>
                            <div style={REPORTING_STYLES.cardSubtitle}>Primary person to contact for work coordination</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body border-1" style={REPORTING_STYLES.cardBody}>
                      {renderCompactDetailRows(reportingToInfo, "reportingto")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
